'use client';

import { getDefaultValueForType } from '@gnsx/genesys.js';
import { type SnapshotIn, t } from 'mobx-state-tree';

import { PropertyType } from '../propertyType';
import { type IProperty } from '../propertyTypes';

import { PropertyBase } from './propertyBase';

import type { ValueChangeHandler } from '../types';
import type { PropertyMetadata } from '@gnsx/genesys.js';
import type { IAnyModelType } from 'mobx-state-tree';


export const ArrayPropertyBuilder = {
  create: (params: SnapshotIn<typeof ArrayProperty> & { value: any[] }) => {
    const property = ArrayProperty.create(params);
    property.setValue(params.value);
    return property;
  }
};

/**
 * Model for array property that can contain other properties
 * NOTE: Use ArrayPropertyBuilder.create() to create an array property
 */
export const ArrayProperty = t.compose('ArrayProperty', PropertyBase,
  t.model({
    type: t.literal(PropertyType.Array),
    properties: t.array(t.late((): IAnyModelType => {
      const { AnyProperty } = require('../propertyTypes');
      return AnyProperty;
    })),
  })
).volatile(() => ({
  value: undefined as any[] | undefined,
  // Store context needed for creating child properties dynamically
  createChildPropertyContext: undefined as {
    onChange?: ValueChangeHandler;
    depth: number;
    isSkipDefaultOnChange: boolean;
    childMeta?: PropertyMetadata;
  } | undefined,
})).actions(self => ({
  setValue(value: any[]) {
    self.value = value;
  },
  addProperty(property: IProperty) {
    self.properties.push(property);
    self.store?.clearPropertyCache();
  },
  setProperties(properties: IProperty[]) {
    self.properties.replace(properties);
    self.store?.clearPropertyCache();
  },
  removeProperty(index: number) {
    self.properties.splice(index, 1);
    self.store?.clearPropertyCache();
  },
  clear() {
    self.properties.clear();
    self.store?.clearPropertyCache();
  },
  /**
   * Store the context needed for creating child properties later
   */
  setCreateChildPropertyContext(context: {
    onChange?: ValueChangeHandler;
    depth: number;
    isSkipDefaultOnChange: boolean;
    childMeta?: PropertyMetadata;
  }) {
    self.createChildPropertyContext = context;
  },
  /**
   * Add a new item to the array with a default/nullish value
   * This will trigger onChange to update the source object
   */
  addNewItem() {
    const currentArray = self.value ?? [];
    // Use childMeta to determine the appropriate default value based on type
    const childMeta = self.createChildPropertyContext?.childMeta;
    const newValue = childMeta ? getDefaultValueForType(childMeta) : getDefaultArrayValue(currentArray);
    const newArray = [...currentArray, newValue];
    self.value = newArray;

    // Create child property for the new item if context is available
    if (self.createChildPropertyContext && self.store && typeof newValue !== 'function') {
      const { createProperty } = require('../propertyFunctions');
      const editorMeta = require('../../core/editorClassMeta');
      const newIndex = newArray.length - 1;
      const childPath = `${self.path}[${newIndex}]`;

      // Check if we have type metadata that can create a property for null/undefined values
      const hasTypeMetadata = childMeta && (
        editorMeta.getPropertyMetadataClassOrAssetType(childMeta) !== undefined ||
        editorMeta.getPropertyMetadataSelectOptions(childMeta) !== undefined
      );

      // Only skip null/undefined if there's no type metadata
      const shouldCreateProperty = newValue !== null && newValue !== undefined || hasTypeMetadata;

      if (shouldCreateProperty) {
        const childProperty = createProperty({
          context: {
            path: childPath,
            parentPath: self.path,
            meta: childMeta,
            store: self.store
          },
          value: newValue,
          onChange: self.createChildPropertyContext.onChange,
          depth: self.createChildPropertyContext.depth + 1,
          disabled: self.disabled,
          isSkipDefaultOnChange: self.createChildPropertyContext.isSkipDefaultOnChange,
          isOverride: false
        });

        if (childProperty) {
          self.properties.push(childProperty);
        }
      }
    }

    // Notify change to trigger the actual value update on the target
    self.notifyValueChange(self.path, newArray, {
      fromPanel: true,
      initial: false,
      property: self as any
    });
  },
  /**
   * Remove an item from the array at the specified index
   * This will shift all subsequent items down and update the UI
   */
  removeItemAt(index: number) {
    const currentArray = self.value ?? [];
    if (index < 0 || index >= currentArray.length) {
      console.warn('[ArrayProperty] Invalid index for removeItemAt:', index);
      return;
    }

    // Create new array without the item at the specified index
    const newArray = [...currentArray.slice(0, index), ...currentArray.slice(index + 1)];
    self.value = newArray;

    if (index < self.properties.length) {
      self.properties.splice(index, 1);
      for (let i = index; i < self.properties.length; i++) {
        const childProperty = self.properties[i];
        const nextPath = `${self.path}[${i}]`;
        updatePropertyPathRecursive(childProperty, nextPath);
      }
    }

    self.store?.clearPropertyCache();

    // Notify change to trigger the actual value update on the target
    self.notifyValueChange(self.path, newArray, {
      fromPanel: true,
      initial: false,
      property: self as any
    });
  }
}));

function updatePropertyPathRecursive(property: IProperty, nextPath: string) {
  const previousPath = property.path;
  (property as any).path = nextPath;

  const nestedProperties = (property as any).properties;
  if (!nestedProperties) return;

  if (Array.isArray(nestedProperties)) {
    nestedProperties.forEach((child: IProperty) => {
      const childNextPath = child.path.startsWith(previousPath)
        ? `${nextPath}${child.path.slice(previousPath.length)}`
        : child.path;
      updatePropertyPathRecursive(child, childNextPath);
    });
    return;
  }

  if (typeof nestedProperties.forEach === 'function') {
    nestedProperties.forEach((child: IProperty) => {
      const childNextPath = child.path.startsWith(previousPath)
        ? `${nextPath}${child.path.slice(previousPath.length)}`
        : child.path;
      updatePropertyPathRecursive(child, childNextPath);
    });
  }
}

/**
 * Fallback function to infer default value from existing array items
 * Only used when childMeta is not available
 */
function getDefaultArrayValue(existingArray: any[]): any {
  // If array is empty, return null
  if (existingArray.length === 0) {
    return null;
  }

  // Find first non-null item to infer type
  const firstItem = existingArray.find(item => item !== null && item !== undefined) ?? existingArray[0];
  const itemType = typeof firstItem;

  switch (itemType) {
    case 'number':
      return 0;
    case 'string':
      return '';
    case 'boolean':
      return false;
    case 'object':
      if (firstItem === null) return null;
      if (Array.isArray(firstItem)) return [];
      return null;
    default:
      return null;
  }
}